import { supabase } from '@/lib/supabase'

/* RLS does the authorization. Every view here is declared `security_invoker =
   true` in 0003, so selecting from them runs the underlying table policies as
   the caller — a member of one pool sees only that pool's rows and a user in no
   pools sees zero. These functions only shape the query. Never add a
   client-side membership filter on top; it drifts from the policy.

   Note what is NOT here: no display_name lookup. `weekly_results` and
   `weekly_winners` carry user_id only, but `season_standings` carries
   display_name and is built from the same weekly_results-joined-profiles set,
   so it covers exactly the same members. The page resolves names from that one
   query rather than embedding profiles into three of them. The embed would also
   type as an array, because the only FK path from these views to profiles is
   pool_members_user_id_fkey with isOneToOne: false. */

export async function fetchSeasonStandings(poolId: string) {
  const { data, error } = await supabase
    .from('season_standings')
    .select(
      'user_id, display_name, season_rank, total_points, total_correct, total_incorrect, total_ties, total_lock_correct, weekly_wins, weeks_played, weeks_forfeited'
    )
    .eq('pool_id', poolId)
    .order('season_rank', { nullsFirst: false })
  if (error) throw error
  return data
}

export async function fetchCompletedWeeks(poolId: string) {
  // weekly_results is one row per member per week, so this comes back with a
  // row per member and has to be collapsed. Querying `weeks` instead would be
  // wrong: weekly_results is already scoped to weeks at or after the pool's
  // start_week, so a mid-season pool must not offer weeks it never played.
  const { data, error } = await supabase
    .from('weekly_results')
    .select('week_id, week_number, week_label')
    .eq('pool_id', poolId)
    .eq('week_has_locked', true)
    .order('week_number', { ascending: false, nullsFirst: false })
  if (error) throw error

  const byWeek = new Map<string, (typeof data)[number]>()
  for (const row of data) {
    if (row.week_id && !byWeek.has(row.week_id)) byWeek.set(row.week_id, row)
  }
  return [...byWeek.values()]
}

export async function fetchWeekResults(poolId: string, weekId: string) {
  // Ordered by points so the caller can assign week rank by position. The views
  // publish season_rank but no week_rank — ranking a single week is the one
  // thing the database does not already do.
  const { data, error } = await supabase
    .from('weekly_results')
    .select(
      'user_id, week_id, week_number, week_label, points, correct_picks, incorrect_picks, tie_games, lock_correct, did_pick, forfeited, tiebreaker_prediction'
    )
    .eq('pool_id', poolId)
    .eq('week_id', weekId)
    .order('points', { ascending: false, nullsFirst: false })
  if (error) throw error
  return data
}

export async function fetchWeekWinners(poolId: string, weekId: string) {
  // weekly_winners is the authority on who won: it requires points > 0 and
  // points = max(points) for the pool-week. Recomputing "highest score" from
  // fetchWeekResults would look equivalent and quietly disagree on a week where
  // nobody scored. Co-winners all come back; the card lists them.
  const { data, error } = await supabase
    .from('weekly_winners')
    .select('user_id, points, week_id, week_number')
    .eq('pool_id', poolId)
    .eq('week_id', weekId)
  if (error) throw error
  return data
}
